<template>
  <div class="w-full bg-green-900 py-2">
    <div class="container mx-auto px-4 max-w-screen-xl">
      <div class="flex items-center justify-center text-white text-sm font-semibold">
        <div>
          <template v-if="hasError && !displayedData">
            <span class="text-red-200">{{ $t('statBar.unableToLoadPoolData') }}</span>
          </template>
          <template v-else>
            {{ $t('statBar.hashrate') }}: 
            <span 
              :class="[
                'transition-colors duration-300 ease-in-out',
                isDataUpdating ? 'text-yellow-300' : 'text-white'
              ]"
            >
              {{ displayedHashrate }}
            </span>
            {{ $t('statBar.lastBlock') }}: 
            <span 
              :class="[
                'transition-colors duration-300 ease-in-out',
                isDataUpdating ? 'text-yellow-300' : 'text-white'
              ]"
            >
              {{ displayedLastBlockNumber }}
            </span>
            <span 
              :class="[
                'transition-colors duration-300 ease-in-out',
                isDataUpdating ? 'text-yellow-300' : 'text-white'
              ]"
            >
              {{ displayedLastBlockTime }}
            </span>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, nextTick } from 'vue'
import { usePoolMetricsQuery, useFoundBlocksQuery } from '@/queries/useStats'
import { useCurrency } from '@/composables/useCurrency'
import { useI18n } from 'vue-i18n'
import { formatHashrate, formatTimeFromSeconds } from '@/utils/hashrate'

// Use centralized currency management
const { selectedCurrency } = useCurrency()
const { t: _t } = useI18n()

const { data: poolMetrics, isLoading: metricsLoading } = usePoolMetricsQuery(selectedCurrency)
const { data: foundBlocksData } = useFoundBlocksQuery({
  coin: selectedCurrency,
  page: 1,
  limit: 1
})

// Reactive state for displayed data (persists old data during loading)
const displayedData = ref<{
  hashrate: string
  lastBlockNumber: string
  lastBlockTime: string
} | null>(null)

// State for animation
const isDataUpdating = ref(false)

// Computed values for current data
const currentHashrate = computed(() => {
  if (!poolMetrics.value) return '0.00 GH/s'
  // API returns hashrate_now in GH/s, convert to MH/s for formatHashrate function
  const hashrateInMh = poolMetrics.value.hashrate_now * 1000 // Convert GH/s to MH/s
  return formatHashrate(hashrateInMh)
})

const currentLastBlockNumber = computed(() => {
  const blocks = foundBlocksData.value?.data
  return blocks && blocks.length > 0 ? blocks[0].height : 'N/A'
})

const currentLastBlockTime = computed(() => {
  const blocks = foundBlocksData.value?.data
  if (!blocks || blocks.length === 0) return ''

  const now = Math.floor(Date.now() / 1000)
  const diff = now - blocks[0].timestamp
  return `(${formatTimeFromSeconds(diff)})`
})

// Displayed data (shows old data until new data is loaded)
const displayedHashrate = computed(() => {
  return displayedData.value?.hashrate || currentHashrate.value
})

const displayedLastBlockNumber = computed(() => {
  return displayedData.value?.lastBlockNumber || currentLastBlockNumber.value
})

const displayedLastBlockTime = computed(() => {
  return displayedData.value?.lastBlockTime || currentLastBlockTime.value
})

const hasError = computed(() => {
  return !poolMetrics.value && !metricsLoading.value && !displayedData.value
})

// Watch for data changes and trigger animation
watch(
  [currentHashrate, currentLastBlockNumber, currentLastBlockTime],
  async (newValues, oldValues) => {
    // Skip if this is the initial load
    if (!displayedData.value) {
      displayedData.value = {
        hashrate: newValues[0],
        lastBlockNumber: String(newValues[1]),
        lastBlockTime: newValues[2]
      }
      return
    }

    // Check if data actually changed
    const [newHashrate, newBlockNumber, newBlockTime] = newValues
    const [oldHashrate, oldBlockNumber, oldBlockTime] = oldValues || ['', '', '']

    const hasChanged = (
      newHashrate !== oldHashrate ||
      newBlockNumber !== oldBlockNumber ||
      newBlockTime !== oldBlockTime
    )

    if (hasChanged) {
      // Trigger update animation
      isDataUpdating.value = true
      
      // Wait for animation to start
      await nextTick()
      
      // Update displayed data after a short delay
      setTimeout(() => {
        displayedData.value = {
          hashrate: newHashrate,
          lastBlockNumber: String(newBlockNumber),
          lastBlockTime: newBlockTime
        }
        
        // Stop animation after data is updated
        setTimeout(() => {
          isDataUpdating.value = false
        }, 300)
      }, 150)
    }
  },
  { immediate: true }
)
</script>
